import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import type {
    IRegDataComparisonResult,
    IRegDataPreviewResult,
    IRegDataStagedChange,
    IWasteReturnTemplateMapping,
} from "../../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { spreadsheetTemplatesService } from "../../../services/spreadsheetTemplates/spreadsheetTemplates.service";
import { getUserFacingErrorMessage, showToastError, showToastSuccess } from "../../../utils";
import { RegDataComparisonCard } from "./RegDataComparisonCard";
import { RegDataApplyConfirmModal } from "./RegDataApplyConfirmModal";
import { RegDataPreviewModal } from "./RegDataPreviewModal";
import { BULK_APPLY_CONFIRM_THRESHOLD, computeStagedChangesBreakdown, countExtractedValuesInMapping } from "./regDataAlignmentDisplay";
import { buildReconciliationDisplayModels } from "./buildReconciliationGrid";
import { revertStagedChange, shouldClearPreviewAfterRevert } from "./regDataAlignmentPreviewFlow";
import {
    buildPreviewRequest,
    hasPendingStagedChanges,
    removeStagedChange,
    resetStagedChanges,
    updateStagedEditedValue,
} from "./regDataAlignmentState";

export interface RegDataComparisonPanelProps {
    hasActiveTemplate: boolean;
    hasMapping: boolean;
    mapping: IWasteReturnTemplateMapping;
    reloadToken?: number;
}

export function RegDataComparisonPanel({
    hasActiveTemplate,
    hasMapping,
    mapping,
    reloadToken = 0,
}: RegDataComparisonPanelProps) {
    const [comparison, setComparison] = useState<IRegDataComparisonResult | null>(null);
    const [stagedChanges, setStagedChanges] = useState<IRegDataStagedChange[]>([]);
    const [previewResult, setPreviewResult] = useState<IRegDataPreviewResult | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [applyLoading, setApplyLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [applyError, setApplyError] = useState<string | null>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [showApplyConfirmModal, setShowApplyConfirmModal] = useState(false);

    const extractedCount = useMemo(() => countExtractedValuesInMapping(mapping.listSources), [mapping.listSources]);
    const canShowPanel = hasActiveTemplate && hasMapping;
    const hasExtractedValues = extractedCount > 0;
    const canApply = hasPendingStagedChanges(stagedChanges);

    const cardModels = useMemo(
        () => (comparison ? buildReconciliationDisplayModels(comparison, stagedChanges) : []),
        [comparison, stagedChanges]
    );

    const loadComparison = useCallback(async () => {
        if (!canShowPanel) return;
        setLoading(true);
        setError(null);
        try {
            const result = await spreadsheetTemplatesService.getRegDataComparison();
            setComparison(result);
        } catch (err: unknown) {
            setComparison(null);
            setError(getUserFacingErrorMessage(err, "Could not load regulatory data comparison."));
        } finally {
            setLoading(false);
        }
    }, [canShowPanel]);

    useEffect(() => {
        if (!canShowPanel) {
            setComparison(null);
            setStagedChanges(resetStagedChanges());
            setPreviewResult(null);
            return;
        }
        loadComparison().catch(() => undefined);
    }, [canShowPanel, loadComparison, reloadToken]);

    const invalidatePreview = () => {
        setPreviewResult(null);
    };

    const runPreviewForStaged = useCallback(async (changes: IRegDataStagedChange[]) => {
        if (shouldClearPreviewAfterRevert(changes)) {
            setPreviewResult(null);
            setPreviewError(null);
            return;
        }

        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const result = await spreadsheetTemplatesService.previewRegDataChanges(buildPreviewRequest(changes));
            setPreviewResult(result);
        } catch (err: unknown) {
            const message = getUserFacingErrorMessage(err, "Could not preview regulatory data changes.");
            setPreviewError(message);
            invalidatePreview();
            showToastError(message);
        } finally {
            setPreviewLoading(false);
        }
    }, []);

    const handleReset = () => {
        setStagedChanges(resetStagedChanges());
        invalidatePreview();
        loadComparison().catch(() => undefined);
    };

    const handlePreview = async () => {
        setShowPreviewModal(true);
        await runPreviewForStaged(stagedChanges);
    };

    const handleRePreview = () => {
        runPreviewForStaged(stagedChanges).catch(() => undefined);
    };

    const handleApply = async () => {
        if (!canApply) return;

        setApplyLoading(true);
        setApplyError(null);
        try {
            const request = buildPreviewRequest(stagedChanges);
            await spreadsheetTemplatesService.applyRegDataChanges(request);
            showToastSuccess("Regulatory data updated successfully.");
            setStagedChanges(resetStagedChanges());
            invalidatePreview();
            setShowPreviewModal(false);
            setShowApplyConfirmModal(false);
            await loadComparison();
        } catch (err: unknown) {
            const message = getUserFacingErrorMessage(err, "Could not apply regulatory data changes.");
            setApplyError(message);
            showToastError(message);
        } finally {
            setApplyLoading(false);
        }
    };

    const applyBreakdown = useMemo(
        () => computeStagedChangesBreakdown(stagedChanges),
        [stagedChanges]
    );

    const requestApply = () => {
        if (!canApply) return;

        if (stagedChanges.length > BULK_APPLY_CONFIRM_THRESHOLD) {
            setShowApplyConfirmModal(true);
            return;
        }

        handleApply().catch(() => undefined);
    };

    const handleUndo = (changeKey: string) => {
        setStagedChanges((current) => removeStagedChange(current, changeKey));
        invalidatePreview();
    };

    const handleRevertFromPreview = async (changeKey: string) => {
        const nextStaged = revertStagedChange(stagedChanges, changeKey);
        setStagedChanges(nextStaged);
        await runPreviewForStaged(nextStaged);
    };

    const handleEditedValueChange = (changeKey: string, editedValue: Record<string, unknown>) => {
        setStagedChanges((current) => updateStagedEditedValue(current, changeKey, editedValue));
        invalidatePreview();
    };

    const handleStagedChangesChange = (changes: IRegDataStagedChange[]) => {
        setStagedChanges(changes);
        invalidatePreview();
    };

    if (!canShowPanel) return null;

    const pendingCount = stagedChanges.length;

    return (
        <Card className="mb-4">
            <Card.Header>Reg Data Alignment</Card.Header>
            <Card.Body>
                <p className="text-muted small">
                    Compare extracted template list values with system regulatory data. Mark rows to update, optionally
                    preview pending changes, then apply updates to regulatory reference data.
                </p>

                {!hasExtractedValues && (
                    <Alert variant="info">
                        No extracted template values are available. Run <strong>Extract List Values</strong> first, then
                        return here to align regulatory data.
                    </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}
                {previewError && <Alert variant="warning">{previewError}</Alert>}
                {applyError && <Alert variant="warning">{applyError}</Alert>}

                {comparison?.warnings && comparison.warnings.length > 0 && (
                    <Alert variant="warning" className="py-2 small">
                        {comparison.warnings.map((warning, index) => (
                            <div key={`${warning.code}-${index}`}>
                                <strong>{warning.code}</strong>: {warning.message}
                            </div>
                        ))}
                    </Alert>
                )}

                {loading ? (
                    <div className="d-flex align-items-center gap-2 text-muted">
                        <Spinner animation="border" size="sm" />
                        Loading comparison…
                    </div>
                ) : (
                    hasExtractedValues &&
                    cardModels.map((card) => (
                        <RegDataComparisonCard
                            key={card.fieldKey}
                            card={card}
                            comparison={comparison}
                            stagedChanges={stagedChanges}
                            onStagedChangesChange={handleStagedChangesChange}
                            onEditedValueChange={handleEditedValueChange}
                            onUndoStaged={handleUndo}
                        />
                    ))
                )}

                <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
                    <Button
                        variant="outline-primary"
                        onClick={() => handlePreview().catch(() => undefined)}
                        disabled={!hasExtractedValues || stagedChanges.length === 0 || previewLoading || applyLoading}
                    >
                        {previewLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Previewing…
                            </>
                        ) : (
                            "Preview Changes"
                        )}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleReset} disabled={loading || applyLoading}>
                        Discard all changes
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => requestApply()}
                        disabled={!hasExtractedValues || !canApply || applyLoading}
                    >
                        {applyLoading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Applying…
                            </>
                        ) : (
                            "Update Reg Data"
                        )}
                    </Button>
                    {pendingCount > 0 && (
                        <span className="small text-muted">
                            {pendingCount} change{pendingCount === 1 ? "" : "s"} pending
                        </span>
                    )}
                </div>
            </Card.Body>

            <RegDataPreviewModal
                show={showPreviewModal}
                loading={previewLoading}
                preview={previewResult}
                cardModels={cardModels}
                revertDisabled={previewLoading}
                onClose={() => setShowPreviewModal(false)}
                onRePreview={handleRePreview}
                onRevertStaged={(changeKey) => handleRevertFromPreview(changeKey).catch(() => undefined)}
            />

            <RegDataApplyConfirmModal
                show={showApplyConfirmModal}
                applying={applyLoading}
                breakdown={applyBreakdown}
                onCancel={() => setShowApplyConfirmModal(false)}
                onConfirm={() => handleApply().catch(() => undefined)}
            />
        </Card>
    );
}
